import { useEffect, useImperativeHandle } from 'react';
import { usePinchZoom } from '../../application/motion/usePinchZoom';

/**
 * One picture in the full-screen viewer: fitted to the screen, and every way of
 * moving it (SPEC-017).
 *
 * Its own component because the gesture state is PER PICTURE — each one has its
 * own scale, its own offset and its own bounds — and a hook per slide is what
 * keeps that true without the viewer above holding an array of five views it
 * would then have to index correctly on every pointer frame.
 *
 * What it hands back up is deliberately thin: a `reset` and a `toggleZoom` the
 * viewer can aim at whichever slide is showing, and one bit saying whether this
 * picture is currently fitted. Everything else stays down here, off React's
 * render path.
 */
export default function PlantViewerSlide({
    slide,
    alt,
    index,
    railRef,
    controlsRef,
    onZoomChange,
}) {
    const { frameRef, pictureRef, fitted, reset, toggleZoom, handlers } = usePinchZoom({ railRef });

    useImperativeHandle(controlsRef, () => ({ reset, toggleZoom }), [reset, toggleZoom]);

    useEffect(() => {
        onZoomChange(index, fitted);
    }, [fitted, index, onZoomChange]);

    return (
        // Focusable, as it was when it was a scroll container (SPEC-008 D-6) —
        // but the arrow keys the platform used to provide with that scrolling
        // are provided by the hook now, because this no longer scrolls at all.
        <li
            className={`plant-viewer__slide plant-viewer__slide--${slide.kind}`
                + (fitted ? '' : ' plant-viewer__slide--zoomed')}
            ref={frameRef}
            tabIndex={0}
            {...handlers}
        >
            <img
                ref={pictureRef}
                src={slide.src}
                srcSet={slide.srcSet ?? undefined}
                // The largest derivative, always: fit is the SMALLEST this
                // picture is ever drawn here, and a pinch immediately asks for
                // more than the file has (SPEC-017, "The cost, stated plainly").
                sizes="100vw"
                alt={alt}
                decoding="async"
                // The gesture on this picture is a pan; a native image drag
                // would end it halfway through and hand back a file instead.
                draggable={false}
            />
        </li>
    );
}
